import React, { useState } from 'react'
import { Button, Card, Col, Form, Input, InputNumber, Modal, Select, Space, Table, Tooltip, message, theme } from 'antd'
import { hexToRGBA } from '../../../lib/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import CustomRow from '../../../reusable-components/CustomRow'

// Placeholder for API integration - replace with actual API calls
const saveMedication = async (patientRef, medication) => {
  // In a real implementation, this would call your backend API

  return { ...medication, id: Math.random().toString(36).substring(7) }
}

const updateMedication = async (patientRef, medicationId, medication) => {
  // In a real implementation, this would call your backend API

  return { ...medication }
}

const deleteMedication = async (patientRef, medicationId) => {
  // In a real implementation, this would call your backend API

  return true
}

const MedicationManagement = ({ medications = [], setMedications, patientRef }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingMedication, setEditingMedication] = useState(null)
  const [form] = Form.useForm()
  const { token } = theme.useToken()

  const frequencyOptions = [
    { label: 'Once daily', value: 'once_daily' },
    { label: 'Twice daily', value: 'twice_daily' },
    { label: 'Three times daily', value: 'thrice_daily' },
    { label: 'Four times daily', value: 'four_times_daily' },
    { label: 'Every other day', value: 'alternate_days' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'As needed', value: 'as_needed' }
  ]

  const handleAddMedication = () => {
    setEditingMedication(null)
    form.resetFields()
    setIsModalOpen(true)
  }

  const handleEditMedication = (record) => {
    setEditingMedication(record)
    form.setFieldsValue({
      name: record.name,
      dosage: record.dosage,
      frequency: record.frequency,
      durationDays: record.durationDays,
      notes: record.notes
    })
    setIsModalOpen(true)
  }

  const handleDeleteMedication = async (record) => {
    try {
      await deleteMedication(patientRef, record.id)
      setMedications(medications.filter((med) => med.id !== record.id))
      message.success('Medication deleted successfully')
    } catch (error) {
      message.error('Failed to delete medication')
    }
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      if (editingMedication) {
        // Update existing medication
        const updatedMedication = await updateMedication(patientRef, editingMedication.id, {
          ...editingMedication,
          ...values
        })
        setMedications(medications.map((med) => (med.id === editingMedication.id ? updatedMedication : med)))
        message.success('Medication updated successfully')
      } else {
        // Add new medication
        const newMedication = await saveMedication(patientRef, {
          ...values,
          adherence: true // Default value
        })
        setMedications([...medications, newMedication])
        message.success('Medication added successfully')
      }
      setIsModalOpen(false)
    } catch (error) {
      // Form validation error or API error
      console.error('Failed to save medication:', error)
    }
  }

  const columns = [
    {
      title: 'Medication',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Dosage',
      dataIndex: 'dosage',
      key: 'dosage'
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      render: (frequency) => {
        const option = frequencyOptions.find((opt) => opt.value === frequency)
        return option ? option.label : frequency
      }
    },
    {
      title: 'Duration (Days)',
      dataIndex: 'durationDays',
      key: 'durationDays'
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title='Edit'>
            <Button type='text' icon={<FontAwesomeIcon icon={faEdit} />} onClick={() => handleEditMedication(record)} />
          </Tooltip>
          <Tooltip title='Delete'>
            <Button
              type='text'
              danger
              icon={<FontAwesomeIcon icon={faTrash} />}
              onClick={() => handleDeleteMedication(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ]

  return (
    <>
      <CustomRow>
        <Col span={24}>
          <Card
            title='Medication Management'
            headStyle={{ background: hexToRGBA(token.colorPrimary, 0.2) }}
            extra={
              <Button type='primary' icon={<FontAwesomeIcon icon={faPlus} />} onClick={handleAddMedication}>
                Add Medication
              </Button>
            }
          >
            <Table dataSource={medications} columns={columns} rowKey='id' />
          </Card>
        </Col>
      </CustomRow>

      <Modal
        title={editingMedication ? 'Edit Medication' : 'Add Medication'}
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={() => setIsModalOpen(false)}
        width={600}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='name'
            label='Medication Name'
            rules={[{ required: true, message: 'Please enter the medication name' }]}
          >
            <Input placeholder='e.g., Amoxicillin, Lisinopril' />
          </Form.Item>

          <Form.Item name='dosage' label='Dosage' rules={[{ required: true, message: 'Please enter the dosage' }]}>
            <Input placeholder='e.g., 500mg, 10mg' />
          </Form.Item>

          <Form.Item
            name='frequency'
            label='Frequency'
            rules={[{ required: true, message: 'Please select the frequency' }]}
          >
            <Select options={frequencyOptions} placeholder='Select frequency' />
          </Form.Item>

          <Form.Item
            name='durationDays'
            label='Duration (Days)'
            rules={[{ required: true, message: 'Please enter the duration' }]}
          >
            <InputNumber min={1} placeholder='e.g., 7, 14, 30' style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name='notes' label='Notes'>
            <Input.TextArea rows={3} placeholder='Additional instructions (e.g., Take with food)' />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default MedicationManagement
